import { Link } from 'react-router-dom';
import type { RiderRunRow } from '@chicare/shared';

import { useRiderRuns } from '@/lib/consoleQueries';
import { useLogout, useSession } from '@/lib/session';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from '@/features/admin/AdminError';

/**
 * A rider's runs for the day.
 *
 * ── THE WHOLE SCREEN IS A LIST OF PLACES TO GO ──────────────────────────────
 *
 * No dashboard, no statistics, no earnings chart. A rider opens this between
 * drops, reads one address, and puts the phone away. Every row is a tap target
 * the size of a thumb, and the address is the largest thing on it.
 *
 * ── WHY THE BAG TOTAL IS AT THE TOP ─────────────────────────────────────────
 *
 * §3.3 caps how much value a rider may carry by tier. Somebody carrying
 * ₦180,000 of medicine should know that is what is in their bag — it changes
 * how carefully a person rides, and it is the number an insurer would ask
 * about first.
 */
export function RunsScreen() {
  const { data, isLoading, isError, error, refetch } = useRiderRuns();
  const { user } = useSession();
  const logout = useLogout();

  if (isError) {
    return <AdminError title="We couldn't load your runs" error={error} onRetry={() => refetch()} />;
  }

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-5">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">Your runs</h1>
          <p className="m-0 mt-0.5 text-[13px] text-muted">{user?.name}</p>
        </div>

        <button
          type="button"
          onClick={() => logout.mutate()}
          className="min-h-11 shrink-0 bg-transparent p-0 text-[12.5px] font-medium text-muted"
        >
          Sign out
        </button>
      </header>

      {data ? (
        <div
          className={cn(
            'mb-4 flex flex-wrap items-baseline justify-between gap-2 rounded-card border p-3.5',
            data.meta.over_cap ? 'border-danger bg-danger-tint' : 'border-line bg-white',
          )}
        >
          <div>
            <p className="m-0 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
              In your bag
            </p>
            <p className="m-0 mt-0.5 font-mono text-[20px] font-bold tabular-nums text-ink">
              {data.meta.bag_value_display}
            </p>
          </div>

          {data.meta.max_bag_value_display ? (
            <p
              className={cn(
                'm-0 text-[12.5px] font-medium',
                data.meta.over_cap ? 'text-danger' : 'text-muted',
              )}
            >
              {data.meta.over_cap ? 'Over your ' : 'Your limit is '}
              {data.meta.max_bag_value_display}
            </p>
          ) : null}
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" radius="card" />
          <Skeleton className="h-28 w-full" radius="card" />
        </div>
      ) : data && data.runs.length === 0 ? (
        <EmptyState
          title="Nothing to deliver"
          body="When a pharmacy hands you a bag it shows up here with the address."
          action={
            <Button variant="ghost" icon="refresh" onClick={() => refetch()}>
              Check again
            </Button>
          }
        />
      ) : (
        <ul className="m-0 list-none space-y-3 p-0">
          {data?.runs.map((run) => (
            <li key={run.reference}>
              <RunRow run={run} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RunRow({ run }: { run: RiderRunRow }) {
  const address = [run.deliver_to?.lga, run.deliver_to?.state].filter(Boolean).join(', ');

  return (
    <Link
      to={`/rider/runs/${run.reference}`}
      className="flex items-center gap-3 rounded-card border border-line bg-white p-4 no-underline"
    >
      <div className="min-w-0 flex-1">
        {/* The street line is the biggest thing on the row: it is what a rider
            is actually looking for when they glance at this. */}
        <p className="m-0 text-[15px] font-bold leading-snug text-ink">
          {run.deliver_to?.line1 ?? 'Address missing'}
        </p>
        {address ? <p className="m-0 mt-0.5 text-[13px] text-muted">{address}</p> : null}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {run.awaiting_payment ? (
            <Badge tone="caution">Waiting on payment</Badge>
          ) : run.ready_for_handover ? (
            <Badge tone="verified">Ready to hand over</Badge>
          ) : (
            <Badge tone="neutral">Not ready</Badge>
          )}
          <span className="font-mono text-[12px] text-muted">{run.value_display}</span>
        </div>
      </div>

      <Icon name="chevron" size={18} className="shrink-0 text-faint" />
    </Link>
  );
}
